import { useEffect, useRef } from 'react';
import { type UseFormGetValues, type UseFormSetValue } from 'react-hook-form';

import { type QuotePlan, type QuotePlanType } from '../consts/quizQuote.const';
import { type QuizQuoteFormValues } from '../utils/quizQuote.schema';
import { type QuotePricingPolicy } from '../utils/quotePricingPolicy';

interface QuoteUsageValues {
  term: number;
  userCount: number;
}

function getUsage(
  getValues: UseFormGetValues<QuizQuoteFormValues>,
): QuoteUsageValues {
  return {
    term: getValues('term'),
    userCount: getValues('user_count'),
  };
}

function normalizeGroupUsage(
  values: QuoteUsageValues,
  minMonthCount?: number,
  minUserCount?: number,
): QuoteUsageValues {
  if (minMonthCount === undefined || minUserCount === undefined) return values;

  return {
    term: Math.max(values.term, minMonthCount),
    userCount: Math.max(values.userCount, minUserCount),
  };
}

function applyUsage(
  getValues: UseFormGetValues<QuizQuoteFormValues>,
  setValue: UseFormSetValue<QuizQuoteFormValues>,
  next: QuoteUsageValues,
) {
  if (getValues('term') !== next.term) {
    setValue('term', next.term, { shouldDirty: true });
  }
  if (getValues('user_count') !== next.userCount) {
    setValue('user_count', next.userCount, { shouldDirty: true });
  }
}

export function useQuotePlanSelection({
  plan,
  planType,
  getValues,
  setValue,
  policy,
}: {
  plan: QuotePlan;
  planType: QuotePlanType;
  getValues: UseFormGetValues<QuizQuoteFormValues>;
  setValue: UseFormSetValue<QuizQuoteFormValues>;
  policy: QuotePricingPolicy | null;
}) {
  const minMonthCount = policy?.groupMinMonthCount;
  const minUserCount = policy?.groupMinUserCount;
  const lastProPlanTypeRef = useRef<QuotePlanType>(planType);
  const lastIndividualUsageRef = useRef<QuoteUsageValues | null>(
    planType === 'individual' ? getUsage(getValues) : null,
  );

  const applyGroupUsage = (usage: QuoteUsageValues) => {
    applyUsage(
      getValues,
      setValue,
      normalizeGroupUsage(usage, minMonthCount, minUserCount),
    );
  };

  const selectPlanType = (next: QuotePlanType) => {
    if (next === planType) return;

    const currentUsage = getUsage(getValues);
    if (plan === 'pro' && planType === 'individual') {
      lastIndividualUsageRef.current = currentUsage;
    }

    if (next === 'group') {
      applyGroupUsage(currentUsage);
    } else if (lastIndividualUsageRef.current) {
      applyUsage(getValues, setValue, lastIndividualUsageRef.current);
    }

    if (plan === 'pro') lastProPlanTypeRef.current = next;
    setValue('plan_type', next, { shouldDirty: true });
  };

  const selectPlan = (next: QuotePlan) => {
    if (next === plan) return;

    const currentUsage = getUsage(getValues);
    if (plan === 'pro') {
      lastProPlanTypeRef.current = planType;
      if (planType === 'individual') {
        lastIndividualUsageRef.current = currentUsage;
      }
    }

    if (next === 'basic') {
      if (planType !== 'individual') {
        setValue('plan_type', 'individual', { shouldDirty: true });
      }
    } else {
      const restoredPlanType = lastProPlanTypeRef.current;
      if (restoredPlanType === 'group') {
        applyGroupUsage(currentUsage);
      } else if (lastIndividualUsageRef.current) {
        applyUsage(getValues, setValue, lastIndividualUsageRef.current);
      }
      if (planType !== restoredPlanType) {
        setValue('plan_type', restoredPlanType, { shouldDirty: true });
      }
    }

    setValue('plan', next, { shouldDirty: true });
  };

  useEffect(() => {
    if (plan !== 'pro' || planType !== 'group') return;
    applyUsage(
      getValues,
      setValue,
      normalizeGroupUsage(getUsage(getValues), minMonthCount, minUserCount),
    );
  }, [getValues, minMonthCount, minUserCount, plan, planType, setValue]);

  return { selectPlan, selectPlanType };
}
